/**
 * Centralized API client configuration
 * Provides configured axios instances for different parts of the application
 */

import { getApiUrl } from "@utils";
import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { attachApiErrorReporting } from "./apiErrorReporting";
import { attachAuthOriginGuard } from "./authOriginGuard";

/**
 * Same 401 recovery as the global axios (see authLoader): silently re-mint the
 * backend JWT and replay the request once, else clear the session and redirect
 * to /login. The silent-auth module (and its store/router import graph) is
 * loaded lazily on the first 401 so that merely importing this factory — e.g.
 * from a node test through the services barrel — doesn't drag in modules with
 * browser-only module-scope side effects.
 */
const attachLazySilentAuthRetry = (client: AxiosInstance): void => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!axios.isAxiosError(error) || error.response?.status !== 401) {
        return Promise.reject(error);
      }
      const [{ handleUnauthorized }, { redirectToLoginOnAuthFailure }] =
        await Promise.all([
          import("@/auth/silentReauth"),
          import("../../router/authLoader"),
        ]);
      return handleUnauthorized(client, error, redirectToLoginOnAuthFailure);
    },
  );
};

/** Client-side request timeout for all auth exchanges (ms). */
export const AUTH_REQUEST_TIMEOUT_MS = 30000;

/** Options for createAuthenticatedClient; headers/baseURL are set by the factory. */
type AuthenticatedClientConfig = Omit<
  AxiosRequestConfig,
  "headers" | "baseURL"
>;

export const getAuthenticatedClientConfig = (
  accessToken: string,
  baseURL = getApiUrl(),
  config: AuthenticatedClientConfig = {},
): AxiosRequestConfig => ({
  timeout: AUTH_REQUEST_TIMEOUT_MS,
  ...config,
  baseURL,
  headers: {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

/** The exact authenticated HTTP capabilities a caller owns. */
export type AuthenticatedClient<Method extends keyof AxiosInstance> = Pick<
  AxiosInstance,
  Method
>;

/** Factory seam for code that needs a bounded subset of the authenticated client. */
export type AuthenticatedClientFactory<Method extends keyof AxiosInstance> = (
  accessToken: string,
) => AuthenticatedClient<Method>;

/**
 * Creates an axios instance with auth headers
 */
export const createAuthenticatedClient = (
  accessToken: string,
  baseURL?: string,
  config?: AuthenticatedClientConfig,
): AxiosInstance => {
  const client = axios.create(
    getAuthenticatedClientConfig(accessToken, baseURL || getApiUrl(), config),
  );
  attachAuthOriginGuard(client);
  attachLazySilentAuthRetry(client);
  // After the retry so only unrecovered failures get logged/reported.
  attachApiErrorReporting(client);
  return client;
};

/** A scoped capability never refreshes into the signed-in employee's credentials. */
export const createCapabilityClient = (
  capability: string,
  baseURL: string,
): AxiosInstance => {
  const client = axios.create(
    getAuthenticatedClientConfig(capability, baseURL),
  );
  attachAuthOriginGuard(client);
  return client;
};

/**
 * Creates an axios instance for coach-specific endpoints
 */
export const createCoachClient = (
  accessToken: string,
  companyId: string,
): AxiosInstance => {
  const client = createAuthenticatedClient(accessToken);

  // Set base URL to include company path for coach endpoints
  client.defaults.baseURL = `${getApiUrl()}/companies/${companyId}/coach`;

  return client;
};

/**
 * Account-scoped sibling of `createCoachClient` for the account-wide Coach view
 * (SD-14223). Same auth/retry/reporting stack; base URL points at the account's
 * `/accounts/{accountId}/coach` routes, which aggregate across every connected
 * company on the account. These routes 404 when the account has not opted in.
 */
export const createAccountCoachClient = (
  accessToken: string,
  accountId: string,
): AxiosInstance => {
  const client = createAuthenticatedClient(accessToken);
  client.defaults.baseURL = `${getApiUrl()}/accounts/${accountId}/coach`;
  return client;
};

/**
 * Generic API request wrapper. Failures are logged/reported by the
 * `attachApiErrorReporting` interceptor and propagate to the caller.
 */
export const apiRequest = async <T>(
  client: AuthenticatedClient<"request">,
  config: AxiosRequestConfig,
): Promise<T> => {
  const response = await client.request<T>(config);
  return response.data;
};
